import React from 'react';
import PropTypes from 'prop-types';

import Storage from './helpers/storage';
import Loading from './components/loading';
import JWTParser from './helpers/jwt';

import User from './models/user';

import { useHistory } from 'react-router-dom';
import { datadogRum } from '@datadog/browser-rum';

export const UserContext = React.createContext({});

export const useUser = () => {
    return React.useContext(UserContext);
};

const CONNECT_STATE_KEY = 'connectDataObject';
export const TOKEN_STORAGE_KEY = 'weezeventAuthToken';
const PATH_STORAGE_KEY = 'weezLoginOriginalPath';

const query_params = ({ oAuthUrl, clientId }) => {
    const { redirect_uri } = window.weezConfig.connect;

    const state = Math.floor(Math.random() * 0x10000000).toString(16);

    // Connect magic
    const connect_state = JSON.stringify({
        state
    });
    window.localStorage.setItem(CONNECT_STATE_KEY, connect_state);

    let url = `client_id=${clientId}&state=${state}`;
    if (redirect_uri) {
        url += `&redirect_uri=${encodeURIComponent(redirect_uri)}`;
    }

    return url;
};

const login_redirect = ({ oAuthUrl, clientId }) => {
    // End connect magic
    let params = query_params({ oAuthUrl, clientId });
    window.location = `${oAuthUrl}/signin?${params}`;
};

const UserContextProvider = ({ token, parsedToken, children }) => {
    const { clientId, oAuthUrl } = window.weezConfig.connect;
    let logoutParams = query_params({ oAuthUrl, clientId });
    const logoutUrl = `${oAuthUrl}/logout?${logoutParams}`;

    const logout = React.useCallback(() => {
        Storage.remove(TOKEN_STORAGE_KEY);
    }, []);

    const user = React.useMemo(() => User.fromJWT(parsedToken), [parsedToken]);
    // Get rights and set user
    React.useEffect(() => {
        if (user?.user_id && datadogRum) {
            datadogRum.setUser({
                id: user.user_id,
                name: `${user.name || ''}${user.firstname ? ` ${user.firstname}` : ''}`,
                email: user.email
            });
        }
    }, [user]);

    // One ring to rule them all
    // One ring to find them
    // One ring to bring them all
    // and in the darkness bind them

    if (!user) {
        return <Loading />;
    }

    return (
        <UserContext.Provider
            value={{
                user,
                token,
                logout,
                logoutUrl,
                login: () => login_redirect({ clientId, oAuthUrl })
            }}
        >
            {children}
        </UserContext.Provider>
    );
};

// Lolilol
UserContextProvider.propTypes = {
    token: PropTypes.string,
    parsedToken: PropTypes.object,
    children: PropTypes.node // Amazing
};

export function SimpleConnect({ children, onToken = () => {} }) {
    const history = useHistory();
    const query = new URLSearchParams(window.location.hash.slice(1));

    // Check current token
    let current_token = Storage.get(TOKEN_STORAGE_KEY);

    // Check access_token and remove it from url

    // Note:
    // If we have an access_token in URL we _assume_ we are coming from accounts
    // There's no reason to have an access token othrwise
    // And we are going to remove it later on
    if (query.get('access_token')) {
        let access_token = query.get('access_token');
        Storage.store(TOKEN_STORAGE_KEY, access_token);
        current_token = access_token;

        // Put query back without access token, without redirecting "on react"
        query.delete('access_token');
        window.history.replaceState(null, null, window.location.pathname + `?${query.toString()}`);

        const originalPath = Storage.pop(PATH_STORAGE_KEY);
        if (originalPath) {
            history.replace(originalPath);
        }
    }

    let parsed_token = {};
    parsed_token = new JWTParser(current_token);

    // Always triggers, 😎
    onToken(current_token, parsed_token);

    // Redirect if needed
    if (!current_token || !parsed_token.valid) {
        const { clientId, oAuthUrl } = window.weezConfig.connect;
        // Store current path
        const currentPath = window.location.pathname + window.location.search;
        Storage.store(PATH_STORAGE_KEY, currentPath);

        // Rediect to accounts
        login_redirect({ clientId, oAuthUrl });
        return null;
    }

    return (
        <UserContextProvider token={current_token} parsedToken={parsed_token}>
            {children}
        </UserContextProvider>
    );
}

// Useless, but hey
SimpleConnect.propTypes = {
    children: PropTypes.node, // children is reserved anyways
    onToken: PropTypes.func // begins with on... 90% chances its a func
};
